import type { AxiosInstance } from 'axios';
import { createHttp, http } from '@/http/axios';
import type {
  ChatMessageDto,
  PostChatMessageInput,
  PostChatReactionInput,
} from '@/features/live/types';
import {
  ANALYTICS_RETRY_DELAYS_MS,
  createAnalyticsEventId,
  isRetryableAnalyticsError,
} from '@/lib/analyticsIngestion';
import {
  nextSubmissionSequence,
  readStoredSubmissionSequence,
  storeSubmissionSequence,
} from '@/lib/interactionCommands';
import {
  browserCanaryStorage,
  isLiveApiEligibleAuthorization,
  parseCanaryPercent,
  readCanaryBucket,
  shouldUseLiveApi,
} from '@/lib/liveApiRouting';

type LiveApiInteraction = 'chatMessage' | 'chatReaction' | 'survey' | 'quiz';

const LIVE_API_URL = import.meta.env.VITE_LIVE_API_URL as string | undefined;
/** Un canary par interaction, à 0 (ou absent) tout reste sur welya-api. */
const CANARY_PERCENTS: Record<LiveApiInteraction, number> = {
  chatReaction: parseCanaryPercent(
    import.meta.env.VITE_LIVE_API_CHAT_REACTION_CANARY_PERCENT as string | undefined,
  ),
  chatMessage: parseCanaryPercent(
    import.meta.env.VITE_LIVE_API_CHAT_MESSAGE_CANARY_PERCENT as string | undefined,
  ),
  survey: parseCanaryPercent(
    import.meta.env.VITE_LIVE_API_SURVEY_CANARY_PERCENT as string | undefined,
  ),
  quiz: parseCanaryPercent(import.meta.env.VITE_LIVE_API_QUIZ_CANARY_PERCENT as string | undefined),
};
const LIVE_API_TIMEOUT_MS = 5000;

let liveApiHttp: AxiosInstance | undefined;
const enabledInteractions = new Map<LiveApiInteraction, boolean>();
let lastSubmissionSequence = 0;

function resolveLiveApiHttp(
  interaction: LiveApiInteraction,
  authorization: string,
): AxiosInstance | null {
  if (!LIVE_API_URL || !isLiveApiEligibleAuthorization(authorization)) return null;
  let enabled = enabledInteractions.get(interaction);
  if (enabled === undefined) {
    enabled = shouldUseLiveApi(LIVE_API_URL, CANARY_PERCENTS[interaction], () =>
      readCanaryBucket(browserCanaryStorage()),
    );
    enabledInteractions.set(interaction, enabled);
  }
  if (!enabled) return null;
  // un refus du token spectateur est définitif : le refresh de session ne le change pas
  liveApiHttp ??= createHttp(LIVE_API_URL, LIVE_API_TIMEOUT_MS, { recoverSession: false });
  return liveApiHttp;
}

function wait(delayMs: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, delayMs));
}

/**
 * Une commande partie vers live-api n'est jamais renvoyée à welya-api : un timeout ne prouve pas
 * l'échec, et welya-api créerait un second message ou une seconde soumission. Les retries gardent le
 * même commandId, que le consumer déduplique.
 */
async function sendCommand(
  liveApi: AxiosInstance,
  method: 'post' | 'put',
  path: string,
  body: object,
  authorization: string,
): Promise<void> {
  const config = { headers: { Authorization: authorization } };
  for (let attempt = 0; ; attempt++) {
    try {
      await liveApi[method](path, body, config);
      return;
    } catch (error) {
      const retryDelayMs = ANALYTICS_RETRY_DELAYS_MS[attempt];
      if (retryDelayMs === undefined || !isRetryableAnalyticsError(error)) throw error;
      await wait(retryDelayMs);
    }
  }
}

/** Le message part vers live-api : il n'arrivera qu'avec son écho Ably, quelques secondes après. */
export function sendsAttendeeChatMessageToLiveApi(authorization: string): boolean {
  return resolveLiveApiHttp('chatMessage', authorization) !== null;
}

/**
 * `null` quand live-api a accepté le message : il n'existe qu'après le consumer, et l'auteur le reçoit
 * par l'écho Ably comme les autres spectateurs. Le `commandId` fourni par l'appelant lui permet de
 * rattacher cet écho au message déjà affiché.
 */
export async function postAttendeeChatMessage(
  webinarId: number,
  sessionIdentifier: string,
  authorization: string,
  input: PostChatMessageInput,
  commandId: string = createAnalyticsEventId(),
): Promise<ChatMessageDto | null> {
  const path = `/public/webinars/${webinarId}/sessions/${sessionIdentifier}/chat/messages`;
  const liveApi = resolveLiveApiHttp('chatMessage', authorization);
  if (!liveApi) {
    const response = await http.post<ChatMessageDto>(path, input, {
      headers: { Authorization: authorization },
    });
    return response.data;
  }
  await sendCommand(liveApi, 'post', path, { ...input, commandId }, authorization);
  return null;
}

/** Réaction éphémère et sans identifiant : un retry la compterait deux fois, elle part une seule fois. */
export async function postChatReaction(
  webinarId: number,
  sessionIdentifier: string,
  authorization: string,
  input: PostChatReactionInput,
): Promise<void> {
  const path = `/public/webinars/${webinarId}/sessions/${sessionIdentifier}/chat/reactions`;
  const config = { headers: { Authorization: authorization } };
  const liveApi = resolveLiveApiHttp('chatReaction', authorization);
  if (!liveApi) {
    await http.post(path, input, config);
    return;
  }
  await liveApi.post(path, { ...input, messageId: Number(input.messageId) }, config);
}

export type SubmissionInput<TAnswer> = {
  sessionId: number;
  answers: TAnswer[];
};

/**
 * `null` quand live-api a accepté la soumission : elle n'est persistée qu'après le consumer, donc un
 * `my-submission` relu tout de suite renverrait encore l'état précédent.
 */
export async function submitInteraction<TResponse, TAnswer>(
  interaction: 'survey' | 'quiz',
  method: 'post' | 'put',
  webinarId: number,
  templateId: number,
  authorization: string,
  input: SubmissionInput<TAnswer>,
): Promise<TResponse | null> {
  const resource = interaction === 'survey' ? 'surveys' : 'quizzes';
  const path = `/public/webinars/${webinarId}/${resource}/${templateId}/submit`;
  const liveApi = resolveLiveApiHttp(interaction, authorization);
  if (!liveApi) {
    const response = await http[method]<TResponse>(path, input, {
      headers: { Authorization: authorization },
    });
    return response.data;
  }
  const storage = browserCanaryStorage();
  lastSubmissionSequence = nextSubmissionSequence(
    Math.max(lastSubmissionSequence, readStoredSubmissionSequence(storage)),
    Date.now(),
  );
  storeSubmissionSequence(storage, lastSubmissionSequence);
  await sendCommand(
    liveApi,
    method,
    path,
    {
      commandId: createAnalyticsEventId(),
      sequence: lastSubmissionSequence,
      answers: input.answers,
    },
    authorization,
  );
  return null;
}
